/**
 * Mailbox: the purchase seam.
 *
 * THIS FILE IS THE ONLY SIMULATED PART OF THE PAYMENT FLOW. Everything around it is real: the price
 * comes from the service (`GET /domains/{domain}/mailbox-price`), the wallet balance and currency are read
 * from the wallet slice, the funding decision and the card dialog are the real UI. What does NOT
 * exist yet is a backend endpoint that mints a mailbox invoice (`casino_upgrade` invoices exist for
 * Product features; nothing creates one for a mailbox), so the calls below stand in for it with a
 * short delay each. Calling the real wallet thunks with an invented invoice id would 404.
 *
 * SWITCH-OVER: replacing these functions with the real thunks is the whole backend change.
 *   createMailboxInvoice -> post a `casino_upgrade` invoice for the mailbox catalog item and
 *                                return its id
 *   settleMailboxFromWallet     -> dispatch `payInvoiceFromWallet({ invoice_id })` and await it
 *   settleMailboxByCard         -> goes away: the real CardPaymentSheet confirms the Stripe intent itself
 *   cancelMailboxInvoice        -> dispatch `cancelInvoice({ invoice_id })`
 * Nothing else in the Scenario C page folder knows the difference.
 *
 * Deliberately Scenario C's own file, the same job as Scenario B's seam and no import between them,
 * so the losing scenario deletes whole.
 */
import { MAILBOX_SIMULATED } from '../../mock-data/mailbox-records';

const wait = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * The seam's ONE knob: make the next settlement reject, so the failure path (the sheet staying open
 * with its error line, nothing dispatched) can be driven in component tests. Part of the simulation
 * and gone with it; a real settlement fails on its own.
 */
let failNextSettlement = false;
export const simulateNextSettlementFailure = (): void => {
  failNextSettlement = true;
};
const maybeFail = (): void => {
  if (!failNextSettlement) return;
  failNextSettlement = false;
  throw new Error('Simulated settlement failure');
};

/**
 * Mint the invoice for one mailbox. SIMULATED: returns a fake id after a short delay. Real version
 * posts a `casino_upgrade` invoice for the mailbox catalog item and returns its id. Called only when
 * Pay is pressed (never at submit), so an abandoned checkout mints nothing.
 */
export const createMailboxInvoice = async (): Promise<{ invoiceId: string }> => {
  await wait(MAILBOX_SIMULATED.createMs);
  return { invoiceId: `mock-mailbox-invoice-${crypto.randomUUID()}` };
};

/**
 * Settle an invoice from the brand wallet. SIMULATED: resolves after a short delay. Real version
 * dispatches `payInvoiceFromWallet({ invoice_id })` and awaits it.
 */
export const settleMailboxFromWallet = async (invoiceId: string): Promise<void> => {
  void invoiceId;
  await wait(MAILBOX_SIMULATED.settleMs);
  maybeFail();
};

/**
 * Settle an invoice by card. SIMULATED: resolves after a short delay. Real version: there is no
 * call here at all; `CardPaymentSheet` confirms the Stripe intent itself and fires `onSuccess`,
 * so this function disappears with the stand-in dialog.
 */
export const settleMailboxByCard = async (invoiceId: string): Promise<void> => {
  void invoiceId;
  await wait(MAILBOX_SIMULATED.settleMs);
  maybeFail();
};

/**
 * Abandon an invoice. SIMULATED: resolves immediately. Real version dispatches
 * `cancelInvoice({ invoice_id })`. Reached for a settlement that fails after the mint, and for a
 * create the provider refuses AFTER the invoice settled (a `409` or a `400`: the flow is pay, then
 * create). Whether the billing system voids or refunds a SETTLED invoice through this call is its
 * own question, open outside this repo; this is the one place that asks it.
 *
 * BEFORE WIRING THIS (deferred review findings, inert only because this is a stand-in):
 *  - THE SERVICE ALREADY CHARGES. ows-mail debits the owner's main wallet itself inside the create
 *    (`POST …/owner-mailbox`; reversed if the create fails, `402 insufficient_credits` when short:
 *    docs/ows-mail/frontend-integration-main.md, "A paid mailbox is charged at creation"). Wiring
 *    the mint and settle below as the SWITCH-OVER list says would charge every paid create twice,
 *    and the card option has no counterpart in the contract. A product decision, open: see
 *    `.claude/discoveries.md`.
 *  - The product rule: a mailbox is billed like any other casino product, and EVERY mailbox produces
 *    an invoice: a zero one for the brand's free one, the service's price for each paid one. The free
 *    path minting no invoice at all is not the intended end state. So what follows concerns a document that exists
 *    for every mailbox, not only the paid ones.
 *  - An UNKNOWN create outcome must not void an invoice. The section's onPay calls this for any
 *    create rejection after settlement, including a non-service one (the 30s create timeout, a
 *    network failure), where the provider may still have created the mailbox: that would leave a
 *    mailbox with its invoice voided. Only a refusal the service actually answered (a `409`, a `400`)
 *    is a known "no mailbox".
 *  - A cancel must not be skipped because the operator left. onPay drops out on a stale flow
 *    generation BEFORE it calls this, and the provisioning screen invites the operator to leave while
 *    the create is still in flight, so a refusal that lands after they left cancels nothing.
 *  - Open, for the billing owner (not answered here): does the invoicing system accept a zero-amount
 *    invoice at all, or does a free mailbox need a different document type?
 */
export const cancelMailboxInvoice = async (invoiceId: string): Promise<void> => {
  void invoiceId;
};
